// The navigation tree (INFORMATION-ARCHITECTURE §1) and its visibility rules (§2).
import {
  Bell,
  Box,
  CircleUser,
  Contact,
  CreditCard,
  FileBarChart,
  Gauge,
  History,
  Inbox,
  KeyRound,
  LayoutGrid,
  LifeBuoy,
  ListTree,
  type LucideIcon,
  Server,
  Settings,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  ToggleRight,
  Users,
  Webhook,
  Rocket,
  DollarSign,
  BellRing,
  Activity,
  Plug,
  Timer,
} from "lucide-react";
import { can, type Capabilities } from "./capabilities";
import { helpHome } from "./help";

export interface NavItem {
  screen: string;
  label: string;
  to: string;
  icon: LucideIcon;
  /** Opens outside the app (the help center), in a new tab. */
  external?: boolean;
  /** Shown when this returns true. */
  visible: (c: Capabilities | undefined) => boolean;
}

export interface NavSection {
  id: string;
  label: string | null;
  items: NavItem[];
}

const always = () => true;
const ownsApp = (c?: Capabilities) => Boolean(c?.ownsApps.length);

export const NAV: NavSection[] = [
  {
    id: "personal",
    label: null,
    items: [
      { screen: "APP-01", label: "Home", to: "/", icon: LayoutGrid, visible: always },
      { screen: "NTF-01", label: "Inbox", to: "/inbox", icon: Inbox, visible: always },
      { screen: "ORG-01", label: "Organizations", to: "/orgs", icon: Users, visible: always },
      { screen: "ACC-01", label: "Account", to: "/account", icon: CircleUser, visible: always },
      { screen: "BIL-01", label: "Plan & Billing", to: "/billing", icon: CreditCard, visible: always },
      { screen: "SET-01", label: "Settings", to: "/settings", icon: Settings, visible: always },
    ],
  },
  {
    id: "admin",
    label: "Administration",
    items: [
      { screen: "USR-01", label: "Users", to: "/admin/users", icon: Contact, visible: (c) => can(c, "users.list") },
      { screen: "ACX-05", label: "Access", to: "/admin/access", icon: ToggleRight, visible: (c) => can(c, "entitlements.manage") || ownsApp(c) },
      { screen: "ROL-01", label: "Roles", to: "/admin/roles", icon: ShieldCheck, visible: (c) => can(c, "users.list") || can(c, "roles.manage") },
      { screen: "AUD-01", label: "Audit Log", to: "/admin/audit", icon: History, visible: (c) => can(c, "audit.view") },
      { screen: "RPT-01", label: "Reports", to: "/admin/reports", icon: FileBarChart, visible: (c) => can(c, "users.list") },
      { screen: "NTF-06", label: "Send notification", to: "/admin/notifications/new", icon: Bell, visible: (c) => can(c, "notifications.send") },
    ],
  },
  {
    id: "ops",
    label: "Operations",
    items: [
      { screen: "OPS-01", label: "Overview", to: "/ops", icon: Gauge, visible: (c) => can(c, "operations.view") },
      { screen: "OPS-02", label: "Traffic & latency", to: "/ops/traffic", icon: Activity, visible: (c) => can(c, "operations.view") },
      { screen: "OPS-03", label: "Logs", to: "/ops/logs", icon: ListTree, visible: (c) => can(c, "operations.view") },
      { screen: "SEC-01", label: "Security events", to: "/ops/security", icon: ShieldAlert, visible: (c) => can(c, "operations.view") },
      { screen: "OPS-04", label: "Alarms", to: "/ops/alarms", icon: BellRing, visible: (c) => can(c, "operations.view") },
      { screen: "OPS-05", label: "Jobs & delivery", to: "/ops/jobs", icon: Timer, visible: (c) => can(c, "operations.view") },
      { screen: "OPS-06", label: "Releases", to: "/ops/releases", icon: Rocket, visible: (c) => can(c, "operations.view") },
      { screen: "OPS-07", label: "Cost", to: "/ops/cost", icon: DollarSign, visible: (c) => can(c, "operations.view") },
      { screen: "CFG-01", label: "Configuration", to: "/ops/config", icon: SlidersHorizontal, visible: (c) => can(c, "operations.view") },
    ],
  },
  {
    id: "dev",
    label: "Developer",
    items: [
      { screen: "APL-01", label: "Applications", to: "/dev/apps", icon: Box, visible: (c) => can(c, "applications.manage") || ownsApp(c) },
      { screen: "KEY-01", label: "API Keys", to: "/dev/keys", icon: KeyRound, visible: (c) => can(c, "api_keys.manage") || ownsApp(c) },
      { screen: "WHK-01", label: "Webhooks", to: "/dev/webhooks", icon: Webhook, visible: (c) => can(c, "webhooks.manage") || ownsApp(c) },
      { screen: "INT-01", label: "Integrations", to: "/dev/integrations", icon: Plug, visible: (c) => can(c, "webhooks.manage") || can(c, "api_keys.manage") },
      { screen: "BIL-04", label: "Tenant", to: "/dev/tenant", icon: Server, visible: (c) => can(c, "tenants.manage") || ownsApp(c) },
    ],
  },
  {
    id: "help",
    label: null,
    items: [{ screen: "HELP", label: "Help", to: helpHome(), icon: LifeBuoy, visible: always, external: true }],
  },
];

export function visibleNav(caps: Capabilities | undefined): NavSection[] {
  return NAV.map((s) => ({ ...s, items: s.items.filter((i) => i.visible(caps)) })).filter((s) => s.items.length);
}
